import React, { useCallback, useEffect, useState } from "react";
import { getCronjobsStatus } from "../../../../hooks/api/cronjob";
import "./apiDebug.css";

const PAGE_SIZE = 10;

const formatDate = (value) => {
  if (!value) return "Sin registro";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Fecha invalida";
  }

  return date.toLocaleString("es-VE");
};

const getActiveStatus = (cronjob) => {
  if (cronjob?.activeStatus) {
    return cronjob.activeStatus;
  }

  return cronjob?.active ? "active" : "inactive";
};

const getRunningStatus = (cronjob) => {
  if (cronjob?.runningStatus) {
    return cronjob.runningStatus;
  }

  return cronjob?.isRunning ? "running" : "stopped";
};

export default function ViewCronStatus() {
  const [inputName, setInputName] = useState("");
  const [inputStatus, setInputStatus] = useState("active");
  const [appliedFilter, setAppliedFilter] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("active");
  const [cronjobs, setCronjobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [error, setError] = useState(null);
  const [emptyMessage, setEmptyMessage] = useState(null);

  const fetchCronjobs = useCallback(async (page = 1, name = "", status = "active") => {
    setLoading(true);
    setError(null);
    setEmptyMessage(null);

    try {
      const response = await getCronjobsStatus({
        page,
        limit: PAGE_SIZE,
        name: name.trim(),
        status,
      });

      const newCronjobs = response?.data?.results || [];
      const total = response?.data?.total || 0;
      const computedTotalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

      setCronjobs(newCronjobs);
      setTotalRecords(total);
      setTotalPages(computedTotalPages);
      setCurrentPage(page);
      setAppliedFilter(name.trim());
      setAppliedStatus(status);

      if (newCronjobs.length === 0) {
        setEmptyMessage(
          name.trim()
            ? "No se encontraron cronjobs para el filtro ingresado."
            : "No hay cronjobs registrados para el estado seleccionado."
        );
      }
    } catch (fetchError) {
      console.error("Error al consultar el estado de cronjobs:", fetchError);
      setCronjobs([]);
      setTotalRecords(0);
      setTotalPages(1);
      setEmptyMessage(null);
      setError(
        fetchError?.response?.data?.message ||
          "No se pudo consultar el estado de los cronjobs."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCronjobs(1, "", "active");
  }, [fetchCronjobs]);

  const handleSearch = () => {
    fetchCronjobs(1, inputName, inputStatus);
  };

  const handleClear = () => {
    setInputName("");
    setInputStatus("active");
    fetchCronjobs(1, "", "active");
  };

  const handlePageChange = (direction) => {
    const newPage =
      direction === "back"
        ? Math.max(1, currentPage - 1)
        : Math.min(totalPages, currentPage + 1);

    fetchCronjobs(newPage, appliedFilter, appliedStatus);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className="cron-debug-container">
      <div className="cron-debug-header">
        <h1 className="cron-debug-title">
          Estado de <strong>Cronjobs</strong>
        </h1>
        <p className="cron-debug-subtitle">
          Consulta si un cron esta activo, si se encuentra ejecutandose y
          cuando fue su ultima actividad.
        </p>
      </div>

      <div className="cron-debug-search-section">
        <div className="search-container">
          <div className="search-input-group">
            <label htmlFor="cronjob-name-input" className="search-label">
              NOMBRE DEL CRON
            </label>
            <input
              id="cronjob-name-input"
              className="search-input"
              type="text"
              value={inputName}
              onChange={(e) => setInputName(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ej: updateWooFromOdoo"
              disabled={loading}
            />
          </div>
          <div className="search-input-group search-input-group--status">
            <label htmlFor="cronjob-status-select" className="search-label">
              ESTADO
            </label>
            <select
              id="cronjob-status-select"
              className="search-input"
              value={inputStatus}
              onChange={(e) => setInputStatus(e.target.value)}
              disabled={loading}
            >
              <option value="active">Activos</option>
              <option value="inactive">Apagados</option>
              <option value="running">En ejecucion</option>
              <option value="stopped">Detenidos</option>
              <option value="all">Todos</option>
            </select>
          </div>
          <button
            className="search-button"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? "Consultando..." : "Buscar"}
          </button>
          <button
            className="search-button"
            onClick={handleClear}
            disabled={loading}
            style={{ backgroundColor: "#6c757d" }}
          >
            Limpiar
          </button>
        </div>

        {totalRecords > 0 && (
          <div className="pagination-info">
            <span>
              Mostrando pagina <strong>{currentPage}</strong> de{" "}
              <strong>{totalPages}</strong>
            </span>
            <span className="total-registros">
              Total: <strong>{totalRecords}</strong> cronjob
              {totalRecords !== 1 ? "s" : ""}
            </span>
            <span className="total-registros">
              Estado: <strong>{
                appliedStatus === "active"
                  ? "Activos"
                  : appliedStatus === "inactive"
                  ? "Apagados"
                  : appliedStatus === "running"
                  ? "En ejecucion"
                  : appliedStatus === "stopped"
                  ? "Detenidos"
                  : "Todos"
              }</strong>
            </span>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => handlePageChange("back")}
              disabled={currentPage === 1 || loading}
            >
              ← Anterior
            </button>
            <span className="pagination-indicator">
              {currentPage} / {totalPages}
            </span>
            <button
              className="pagination-btn"
              onClick={() => handlePageChange("next")}
              disabled={currentPage >= totalPages || loading}
            >
              Siguiente →
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className="cron-debug-results">
        {loading ? (
          <div className="loading-container">
            <div className="loader"></div>
            <p className="loading-text">Consultando cronjobs...</p>
          </div>
        ) : cronjobs.length > 0 ? (
          <div className="results-list">
            {cronjobs.map((cronjob, index) => {
              const activeStatus = getActiveStatus(cronjob);
              const runningStatus = getRunningStatus(cronjob);

              return (
                <div className="api-data-viewer" key={cronjob._id || index}>
                  <div className="record-header">
                    <div className="record-header-content">
                      <h2 className="record-title">{cronjob.name}</h2>
                      <span className="record-index">
                        #{index + 1 + (currentPage - 1) * PAGE_SIZE}
                      </span>
                    </div>
                    <div className="record-badges">
                      <span
                        className={`status-badge ${
                          activeStatus === "active"
                            ? "status-active"
                            : "status-inactive"
                        }`}
                      >
                        {activeStatus === "active" ? "Activo" : "Inactivo"}
                      </span>
                      <span
                        className={`status-badge ${
                          runningStatus === "running"
                            ? "status-active"
                            : "status-inactive"
                        }`}
                      >
                        {runningStatus === "running"
                          ? "En ejecucion"
                          : "Detenido"}
                      </span>
                    </div>
                  </div>

                  <div className="collapsible-content">
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">ID</span>
                        <span className="detail-value detail-code">
                          {cronjob._id || "Sin ID"}
                        </span>
                      </div>
                      {/* <div className="detail-item">
                        <span className="detail-label">Nombre</span>
                        <span className="detail-value detail-code">
                          {cronjob.name}
                        </span>
                      </div> */}
                      <div className="detail-item">
                        <span className="detail-label">Creado</span>
                        <span className="detail-value detail-time detail-code">
                          {formatDate(cronjob.createdAt)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Ultima actualizacion</span>
                        <span className="detail-value detail-time detail-code">
                          {formatDate(cronjob.updatedAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : !error && !loading ? (
          <div className="no-results">
            <p>
              {emptyMessage ||
                (appliedFilter
                  ? "No se encontraron cronjobs para el filtro aplicado."
                  : "No hay cronjobs para mostrar en el estado seleccionado.")}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
